import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ConsumerPortalApiService } from '../../services/consumer-portal-api.service';
import { GalleryResponse, OrgGalleryImage } from '../models/ai-image.model';

@Injectable()
export class OrgImageGalleryService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly imagesSubject = new BehaviorSubject<OrgGalleryImage[]>([]);
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    private readonly pageSubject = new BehaviorSubject<number>(1);
    private readonly totalPagesSubject = new BehaviorSubject<number>(0);
    private readonly errorSubject = new BehaviorSubject<string | null>(null);

    readonly images$ = this.imagesSubject.asObservable();
    readonly loading$ = this.loadingSubject.asObservable();
    readonly page$ = this.pageSubject.asObservable();
    readonly totalPages$ = this.totalPagesSubject.asObservable();
    readonly error$ = this.errorSubject.asObservable();

    readonly hasNext$: Observable<boolean> = combineLatest([this.pageSubject, this.totalPagesSubject]).pipe(map(([page, totalPages]) => page < totalPages));

    readonly hasPrev$: Observable<boolean> = this.pageSubject.pipe(map(page => page > 1));

    readonly isEmpty$: Observable<boolean> = combineLatest([this.imagesSubject, this.loadingSubject]).pipe(map(([images, loading]) => !loading && images.length === 0));

    private currentImageType = '';
    private readonly pageSize = 12;

    private readonly http = inject(HttpClient);
    private readonly cpApi = inject(ConsumerPortalApiService);

    loadGallery(imageType: string, page = 1): void {
        this.currentImageType = imageType;
        this.pageSubject.next(page);
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        const params = new HttpParams().set('imageType', imageType).set('page', page.toString()).set('pageSize', this.pageSize.toString());

        const url = `${this.cpApi.apiRoute}/ai-image/gallery`;
        const headers = new HttpHeaders(this.cpApi.requestAuthHeaders);

        this.http
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .get<{ Records: GalleryResponse }>(url, { params, headers })
            .pipe(
                map(result => result.Records),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: response => {
                    this.imagesSubject.next(response.images);
                    this.totalPagesSubject.next(response.totalPages);
                    this.loadingSubject.next(false);
                },
                error: () => {
                    this.errorSubject.next('galleryLoadError');
                    this.loadingSubject.next(false);
                }
            });
    }

    nextPage(): void {
        const current = this.pageSubject.getValue();
        const total = this.totalPagesSubject.getValue();
        if (current < total) {
            this.loadGallery(this.currentImageType, current + 1);
        }
    }

    prevPage(): void {
        const current = this.pageSubject.getValue();
        if (current > 1) {
            this.loadGallery(this.currentImageType, current - 1);
        }
    }

    refresh(): void {
        if (this.currentImageType) {
            this.loadGallery(this.currentImageType, this.pageSubject.getValue());
        }
    }

    reset(): void {
        this.imagesSubject.next([]);
        this.loadingSubject.next(false);
        this.pageSubject.next(1);
        this.totalPagesSubject.next(0);
        this.errorSubject.next(null);
        this.currentImageType = '';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
